import React, { useCallback, useState, useEffect } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import InfoIcon from '@mui/icons-material/Info'
import EditIcon from '@mui/icons-material/Edit'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { Dropdown } from 'primereact/dropdown'
import { Calendar } from 'primereact/calendar'
import { Checkbox } from 'primereact/checkbox'
import { horsesState } from '../../../../state/Horses.state'
import { appState } from '../../../../state/App.state'
import { Loading } from '../../../loading/Loading'
import { useLocalState } from '../../../../shared/UseLocalState'
import { BookedService, Horse, ServiceDto, Starter } from '../../../../api'
import { format } from 'date-fns'
import { registrationState } from '../Registration.state'
import { formatDate } from '../../../../shared/format-date'
import { ServiceSelectionPanel } from './ServiceSelectionPanel'
import { actualShowState } from '../../../../state/ActualShow.state'
import { authState, handleUnauthorized } from '../../../../state/Auth.state'

export const HorsesScreen = () => {
  /* STATE */
  const bookerId = authState.user$.value?.registerId
  const horses = useLocalState<Horse[]>(horsesState.horses$, horsesState.horses$.value)
  const selectedHorse = useLocalState<Horse | null>(registrationState.selectedHorse$, registrationState.selectedHorse$.value)
  const [detail, setDetail] = useState<Horse | null>()
  const [detailEdit, setDetailEdit] = useState<{ size?: number | null; isBigHorse?: boolean }>({})
  const [detailNames, setDetailNames] = useState<{ owner?: string; breeder?: string }>({})
  const isOwner = !!detail && !!bookerId && detail.ownerRegId === bookerId
  const [editHorse, setEditHorse] = useState<Horse | null>(null)
  const [editForm, setEditForm] = useState<Partial<Horse>>({})
  const [breeds, setBreeds] = useState<{ id: number; name: string }[]>([])
  const [colors, setColors] = useState<{ id: number; name: string }[]>([])
  const [regId, setRegId] = useState<string>('')
  const [igvKennziffer, setIgvKennziffer] = useState<string>('')
  const [igvAccessCode, setIgvAccessCode] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const additionalHorses = useLocalState<Horse[]>(registrationState.additionalHorses$, registrationState.additionalHorses$.value)
  const selectedStarter = useLocalState<Starter | null>(registrationState.selectedStarter$, registrationState.selectedStarter$.value)
  const actualShowBookedServices = useLocalState(actualShowState.actualShowBookedServices$, actualShowState.actualShowBookedServices$.value)
  const showServices = useLocalState<ServiceDto[]>(actualShowState.actualShowServices$, actualShowState.actualShowServices$.value)

  /* DERIVED STATE */
  const bookedHorseServices = React.useMemo(
    () =>
      actualShowBookedServices?.filter((bs) => {
        const svcType = showServices?.find((so) => so?.id === bs?.serviceId)?.type
        return (svcType === 'H' || svcType === 'I') && bs?.horseId === selectedHorse?.registerId
      }),
    [actualShowBookedServices, showServices, selectedHorse]
  )

  /* LOGIC */
  const openEditDialog = useCallback((horse: Horse) => {
    setEditHorse(horse)
    const birthday = horse.birthday ? new Date(horse.birthday) : null
    if (birthday) birthday.setMinutes(birthday.getMinutes() - birthday.getTimezoneOffset())
    setEditForm({
      name: horse.name ?? '',
      gender: horse.gender ?? '',
      mother: horse.mother ?? '',
      father: horse.father ?? '',
      owner: horse.owner ?? '',
      stockSize: horse.stockSize ?? undefined,
      isBigHorse: horse.isBigHorse ?? 0,
      birthday,
    })
  }, [])

  const saveHorseEdit = useCallback(() => {
    if (!editHorse?.registerId) return
    const cfg = authState.apicfg()
    if (!cfg.apiKey) return
    setLoading(true)
    Promise.resolve(cfg.apiKey('X-API-KEY')).then((key) => {
      const headers: Record<string, string> = { 'X-API-KEY': key, 'Content-Type': 'application/json' }
      const body = {
        ...editForm,
        birthday: editForm.birthday instanceof Date ? editForm.birthday.toISOString().substring(0, 10) : editForm.birthday,
      }
      fetch(`${cfg.basePath}/api/Horse/${encodeURIComponent(editHorse.registerId!)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
      })
        .then((r) => {
          if (!r.ok) throw new Error('Fehler beim Speichern')
          appState.addToast('Pferd erfolgreich aktualisiert', 'success')
          setEditHorse(null)
          const currentIds = horsesState.horses$.value.map((h) => h.registerId!).filter(Boolean)
          if (currentIds.length > 0) horsesState.fetchHorses(currentIds)
        })
        .catch(() => appState.addToast('Speichern fehlgeschlagen!', 'error'))
        .finally(() => setLoading(false))
    })
  }, [editHorse, editForm])

  const saveDetailEdit = useCallback(() => {
    if (!detail?.registerId) return
    const cfg = authState.apicfg()
    setLoading(true)
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = authState.accessToken$.value
    if (token) headers['Authorization'] = `Bearer ${token}`
    const guestKey = authState.guestMode$.value ? localStorage.getItem('guestApiKey') : null
    if (guestKey) headers['X-API-KEY'] = guestKey
    fetch(`${cfg.basePath}/api/Horse/${encodeURIComponent(detail.registerId!)}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ ...detail, size: detailEdit.size, isBigHorse: detailEdit.isBigHorse, stockSize: undefined }),
    })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { handleUnauthorized(r.status); return }
        if (!r.ok) throw new Error('Fehler beim Speichern')
        appState.addToast('Pferd erfolgreich aktualisiert.', 'success')
        const updatedHorse: Horse = { ...detail, size: detailEdit.size, isBigHorse: detailEdit.isBigHorse ? 1 : 0 }
        horsesState.horses$.next(horsesState.horses$.value.map((h) => h.registerId === detail.registerId ? updatedHorse : h))
        if (registrationState.selectedHorse$.value?.registerId === detail.registerId)
          registrationState.selectedHorse$.next(updatedHorse)
        setDetail(null)
      })
      .catch(() => appState.addToast('Speichern fehlgeschlagen!', 'error'))
      .finally(() => setLoading(false))
  }, [detail, detailEdit])

  const getHorseByRegId = useCallback(() => {
    if ([...horses, ...additionalHorses]?.some((horse) => horse?.registerId === regId))
      return appState.addToast('Pferd bereits in Tabelle!', 'warning')
    setLoading(true)
    horsesState
      .fetchHorseById(regId)
      .then((horse) => {
        appState.addToast('Pferd gefunden: ' + horse?.name, 'info')
        registrationState.additionalHorses$.next([...registrationState.additionalHorses$.value, horse])
        registrationState.selectedHorse$.next(horse)
      })
      .catch(() => {
        appState.addToast('Pferd mit RegId ' + regId + ' nicht gefunden', 'error')
      })
      .finally(() => setLoading(false))
  }, [regId, horses, additionalHorses])

  const getHorseByIgv = useCallback(() => {
    const kennziffer = parseInt(igvKennziffer, 10)
    if (isNaN(kennziffer) || !igvAccessCode.trim()) return
    const alreadyIn = [...horses, ...additionalHorses]?.find(
      (h) => h?.igvKennziffer === kennziffer
    )
    if (alreadyIn) return appState.addToast('Pferd bereits in Tabelle!', 'warning')
    setLoading(true)
    horsesState
      .fetchHorseByIgv(kennziffer, igvAccessCode.trim())
      .then((horse) => {
        appState.addToast('Pferd gefunden: ' + horse?.name, 'info')
        registrationState.additionalHorses$.next([...registrationState.additionalHorses$.value, horse])
        registrationState.selectedHorse$.next(horse)
      })
      .catch(() => {
        appState.addToast('Pferd mit IGV-Kennziffer ' + igvKennziffer + ' nicht gefunden', 'error')
      })
      .finally(() => setLoading(false))
  }, [igvKennziffer, igvAccessCode, horses, additionalHorses])

  /* EFFECTS */
  useEffect(() => {
    const cfg = authState.apicfg()
    if (!cfg.apiKey) return
    Promise.resolve(cfg.apiKey('X-API-KEY')).then((key) => {
      const headers: Record<string, string> = { 'X-API-KEY': key }
      fetch(`${cfg.basePath}/api/Horse/breeds`, { headers })
        .then((r) => r.json())
        .then(setBreeds)
        .catch(() => {})
      fetch(`${cfg.basePath}/api/Horse/colors`, { headers })
        .then((r) => r.json())
        .then(setColors)
        .catch(() => {})
    })
  }, [])

  // Schließe Info-Dialog bei Browser-Back
  useEffect(() => {
    if (!detail) { setDetailNames({}); return }
    const cfg = authState.apicfg()
    const token = authState.accessToken$.value
    const guestKey = authState.guestMode$.value ? localStorage.getItem('guestApiKey') : null
    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    if (guestKey) headers['X-API-KEY'] = guestKey
    const resolveName = (regId: string) =>
      fetch(`${cfg.basePath}/api/Person/register/${encodeURIComponent(regId)}`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(p => p ? [p.firstName, p.lastName].filter(Boolean).join(' ') : undefined)
        .catch(() => undefined)
    const promises: Promise<void>[] = []
    if (detail.ownerRegId) promises.push(resolveName(detail.ownerRegId).then(name => setDetailNames(n => ({ ...n, owner: name }))))
    if (detail.breederRegId) promises.push(resolveName(detail.breederRegId).then(name => setDetailNames(n => ({ ...n, breeder: name }))))
  }, [detail])
  useEffect(() => {
    if (!detail) return
    // kein popstate-Handler – Registration-Guard übernimmt Browser-Back
  }, [detail])

  // Synchronisiere Pferd-Auswahl bei Starter-Wechsel
  useEffect(() => {
    if (!selectedStarter) return
    const horseId = selectedStarter.horseRegisterId
    const allHorses = [...horses, ...additionalHorses]
    const found = allHorses.find((h) => h?.registerId === horseId)
    if (found) {
      registrationState.selectedHorse$.next(found)
    }
  }, [selectedStarter, additionalHorses, horses])

  /* TEMPLATE */
  return (
    <>
      <Loading loading={loading} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div>
          <Dialog
            visible={!!editHorse}
            resizable={false}
            draggable={false}
            style={{ maxWidth: '92vw', minWidth: '320px' }}
            header={`Pferd bearbeiten: ${editHorse?.name ?? ''}`}
            onHide={() => setEditHorse(null)}
            baseZIndex={99999999999999}
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <Button label='Abbrechen' outlined onClick={() => setEditHorse(null)} />
                <Button label='Speichern' onClick={saveHorseEdit} />
              </div>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <InputText
                placeholder='Name'
                value={editForm.name ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Dropdown
                placeholder='Geschlecht'
                value={editForm.gender ?? null}
                options={[
                  { label: 'Stute', value: 'S' },
                  { label: 'Hengst', value: 'H' },
                  { label: 'Wallach', value: 'W' }
                ]}
                optionLabel='label'
                optionValue='value'
                onChange={(e) => setEditForm((f) => ({ ...f, gender: e.value }))}
              />
              <Calendar
                placeholder='Geburtstag'
                value={editForm.birthday instanceof Date ? editForm.birthday : null}
                onChange={(e) => setEditForm((f) => ({ ...f, birthday: e.value as Date ?? undefined }))}
                dateFormat='dd.mm.yy'
                showIcon
              />
              <InputText
                placeholder='Mutter'
                value={editForm.mother ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, mother: e.target.value }))}
              />
              <InputText
                placeholder='Vater'
                value={editForm.father ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, father: e.target.value }))}
              />
              <InputText
                placeholder='Besitzer'
                value={editForm.owner ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, owner: e.target.value }))}
              />
              <InputNumber
                placeholder='Stockmaß (cm)'
                value={editForm.stockSize ?? null}
                onValueChange={(e) => setEditForm((f) => ({ ...f, stockSize: e.value ?? undefined }))}
                suffix=' cm'
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Checkbox
                  inputId='editIsBigHorse'
                  checked={!!editForm.isBigHorse}
                  onChange={(e) => setEditForm((f) => ({ ...f, isBigHorse: e.checked ? 1 : 0 }))}
                />
                <label htmlFor='editIsBigHorse'>Großpferd</label>
              </div>
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'block' }}>IGV-Kennziffer</label>
                <InputText
                  value={editHorse?.igvKennziffer != null ? String(editHorse.igvKennziffer) : ''}
                  disabled
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'block' }}>Access Code</label>
                <InputText
                  value={editHorse?.accessCode ?? ''}
                  disabled
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          </Dialog>
          <Dialog
            visible={!!detail}
            resizable={false}
            draggable={false}
            style={{ maxWidth: '92vw', minWidth: '300px' }}
            header={detail?.name}
            onHide={() => setDetail(null)}
            baseZIndex={99999999999999}
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <Button label='Abbrechen' outlined onClick={() => setDetail(null)} />
                <Button label='Speichern' onClick={saveDetailEdit}
                  disabled={!isOwner || (detailEdit.size === (detail?.size ?? undefined) && detailEdit.isBigHorse === !!detail?.isBigHorse)} />
              </div>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.88rem' }}>
              {[
                { label: 'Geburtstag', value: detail?.birthday && formatDate(detail?.birthday) },
                { label: 'Rasse', value: detail?.breed },
                { label: 'Farbe', value: detail?.color },
                { label: 'Mutter', value: detail?.mother },
                { label: 'Vater', value: detail?.father },
                { label: 'Besitzer', value: detailNames.owner ?? detail?.owner },
                { label: 'Züchter', value: detailNames.breeder ?? detail?.breeder },
                { label: 'Lebensnummer', value: detail?.lNr },
                { label: 'RegistrierId', value: detail?.registerId },
                { label: 'IGV-Kennziffer', value: detail?.igvKennziffer },
                { label: 'Access Code', value: detail?.accessCode },
              ].map(({ label, value }) => value != null && value !== '' && (
                <div key={label} style={{ display: 'flex', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '7rem' }}>{label}</span>
                  <span>{String(value)}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', minWidth: '7rem' }}>Stockmaß</span>
                <InputNumber
                  value={detailEdit.size ?? null}
                  onValueChange={(e) => {
                    const val = e.value ?? undefined
                    let isBigHorse = detailEdit.isBigHorse ?? false
                    if (val == null || val < 144) isBigHorse = false
                    else if (val > 148) isBigHorse = true
                    setDetailEdit((f) => ({ ...f, size: val, isBigHorse }))
                  }}
                  min={0}
                  max={300}
                  maxFractionDigits={0}
                  suffix=' cm'
                  inputStyle={{ width: '5rem' }}
                  disabled={!isOwner}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span style={{ fontWeight: 'bold', minWidth: '7rem' }}>Großpferd</span>
                <Checkbox
                  inputId='detailIsBigHorse'
                  checked={!!detailEdit.isBigHorse}
                  disabled={!isOwner || !detailEdit.size || detailEdit.size < 144 || detailEdit.size > 148}
                  onChange={(e) => setDetailEdit((f) => ({ ...f, isBigHorse: e.checked ?? false }))}
                />
              </div>
            </div>
          </Dialog>
          <DataTable
            showSelectAll={false}
            showHeaders={false}
            value={[...horses, ...additionalHorses]}
            selectionMode={'single'}
            selection={selectedHorse}
            onSelectionChange={(e) => registrationState.selectedHorse$.next(e.value)}
            isDataSelectable={() => !selectedStarter}
          >
            <Column selectionMode='single' bodyStyle={{ width: '3rem' }} />
            <Column
              body={(horse: Horse) => (
                <div>
                  <p style={{ marginBottom: '0.5rem' }}>{horse?.name}</p>
                  <p>{horse?.birthday && format(new Date(horse.birthday), 'dd.MM.yyyy')}</p>
                </div>
              )}
            />
            <Column
              headerStyle={{ width: '3rem' }}
              body={(horse: Horse) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <InfoIcon
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      setDetail(horse)
                      setDetailEdit({ size: horse.size ?? undefined, isBigHorse: !!horse.isBigHorse })
                    }}
                      />{/*
                  <EditIcon
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openEditDialog(horse)}
                  />*/}
                </div>
              )}
            />
          </DataTable>
        </div>
        <div>
          <p style={{ marginBottom: '0.5rem' }}>
            <b>Pferd über Registrierungs ID hinzufügen</b>
          </p>
          <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
            <InputText
              value={regId}
              onChange={(e) => setRegId(e?.target?.value ?? '')}
              style={{ flex: 1 }}
              placeholder='Registrierungs ID eingeben ...'
              disabled={!!selectedStarter}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && regId?.length > 1) getHorseByRegId()
              }}
            />
            <Button onClick={getHorseByRegId} disabled={regId?.length < 1 || !!selectedStarter} label='Suchen' />
          </div>
          <div style={{ display: 'flex' }}>
            <a href={selectedStarter ? undefined : '#/horses'} style={{ flex: 1 }}>
              <Button style={{ width: '100%', textAlign: 'center' }} outlined label='Neues Pferd registrieren' disabled={!!selectedStarter} />
            </a>
          </div>
        </div>
        <div>
          <p style={{ marginBottom: '0.5rem' }}>
            <b>Pferd über IGV-Kennziffer und Zugangscode hinzufügen</b>
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <InputText
              value={igvKennziffer}
              onChange={(e) => setIgvKennziffer(e?.target?.value ?? '')}
              style={{ width: '5rem', flexShrink: 0 }}
              placeholder='Kennziffer'
              maxLength={4}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && igvKennziffer.length > 0 && igvAccessCode.length > 0) getHorseByIgv()
              }}
            />
            <InputText
              value={igvAccessCode}
              onChange={(e) => setIgvAccessCode(e?.target?.value ?? '')}
              style={{ flex: 1 }}
              placeholder='Zugangscode ...'
              onKeyDown={(e) => {
                if (e.key === 'Enter' && igvKennziffer.length > 0 && igvAccessCode.length > 0) getHorseByIgv()
              }}
            />
            <Button
              onClick={getHorseByIgv}
              disabled={igvKennziffer.length < 1 || igvAccessCode.length < 1 || !!selectedStarter}
              label='Suchen'
            />
          </div>
        </div>
      </div>

      {/* Schon gebuchte Services H/I des ausgewählten Pferdes */}
      {bookedHorseServices?.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3>Gebuchte Services für {selectedHorse?.name}</h3>
          <DataTable value={bookedHorseServices} style={{ width: '100%' }}>
            <Column
              header='Service'
              body={(service: BookedService) => showServices?.find((s) => s?.id === service?.serviceId)?.name}
            />
            <Column
              header='Menge'
              headerStyle={{ width: '4rem', textAlign: 'right' }}
              bodyStyle={{ width: '4rem', textAlign: 'right' }}
              body={(service: BookedService) => service?.quantity}
            />
            <Column
              header='Preis'
              headerStyle={{ width: '5rem', textAlign: 'right' }}
              bodyStyle={{ width: '5rem', textAlign: 'right', whiteSpace: 'nowrap' }}
              body={(service: BookedService) => service?.totalPrice?.toFixed(2).replace('.', ',') + '\u00a0€'}
            />
          </DataTable>
        </div>
      )}

      {/* Services Typ I und H wählbar – Impfung und Unterkunft Pferd */}
      <ServiceSelectionPanel types={['I', 'H']} title='Services für das Pferd:' disabled={!!selectedStarter} />
    </>
  )
}
